import React from 'react'
import styled from 'styled-components'
import {injectIntl} from 'react-intl'
import Tooltip from '@mui/material/Tooltip'
import {FormatBold, FormatUnderlined, FormatItalic, Security, Link, Reorder, TextFormat, Palette,
	People, HelpOutline, Assignment, Code, Crop169, Event, FormatListBulleted, FormatListNumbered} from '@mui/icons-material'
import {EditorState, ContentState, RichUtils, AtomicBlockUtils, convertToRaw,
    SelectionState, Modifier} from 'draft-js'
import {onDraftEditorCopy, onDraftEditorCut, handleDraftEditorPastedText} from 'draftjs-conductor'
import Editor from '@draft-js-plugins/editor'
import createMentionPlugin from '@draft-js-plugins/mention'
import draftToHtml from 'draftjs-to-html'
import htmlToDraft from 'html-to-draftjs'
import 'draft-js/dist/Draft.css'
import '@draft-js-plugins/mention/lib/plugin.css'
import ButtonControl from './ButtonControl'
import PublicEntityControl from './PublicEntityControl'
import RegulationControl from './RegulationControl'
import QuestionControl from './QuestionControl'
import SignatureControl from './SignatureControl'
import CodeControl from './CodeControl'
import EventControl from './EventControl'
import LinkDialog from './LinkDialog'
import ButtonDialog from './ButtonDialog'
import QuestionDialog from './QuestionDialog'
import SignatureDialog from './SignatureDialog'
import PublicEntityDialog from './PublicEntityDialog'
import RegulationDialog from './RegulationDialog'
import CodeDialog from './CodeDialog'
import EventDialog from './EventDialog'
import FontPopover from './FontPopover'
import ColorPopover from './ColorPopover'
import Entry from './MentionEntry'
import {colors as colorStyle} from 'utils/enums/colors'

const Container = styled.div`
    margin-bottom: 20px;
    position: relative;

    .public-DraftEditor-content {
        min-height: 200px;
    }

    figure {
        margin: 0;
    }
`

const Content = styled.div`
    border: 1px solid rgba(0, 0, 0, 0.42);
    border-top: none;
    border-radius: 2px;
    padding: 20px;
    min-height: 200px;
    max-height: 450px;
    overflow: auto;
    position: relative;
    box-sizing: border-box;
`

const Panel = styled.div`
    box-shadow: 0 3px 5px 0 #cadce0;
    display: flex;
    align-items: center;
`

const Action = styled.span`
    display: flex;
    margin-right: 15px;
    margin-top: 5px;
    margin-bottom: 5px;
    align-items: center;
    cursor: pointer;

    :first-child {
        margin-left: 15px;
    }
`

const Media = (props) => {
    const entity = props.contentState.getEntity(props.block.getEntityAt(0))
    switch (entity.getType()) {
        case controls.button:
            return <ButtonControl
                data={entity.getData()}
                onEdit={props.blockProps.editControl}
                type={entity.getType()}
                id={props.blockProps.id}
            />

        case controls.publicform:
            return <PublicEntityControl
                data={entity.getData()}
                onEdit={props.blockProps.editControl}
                type={entity.getType()}
                id={props.blockProps.id}
            />

        case controls.publictable:
            return <PublicEntityControl
                data={entity.getData()}
                onEdit={props.blockProps.editControl}
                type={entity.getType()}
                id={props.blockProps.id}
            />

        case controls.gdpr:
            return <RegulationControl
                data={entity.getData()}
                onEdit={props.blockProps.editControl}
                type={entity.getType()}
                id={props.blockProps.id}
            />

        case controls.question:
            return <QuestionControl
                data={entity.getData()}
                onEdit={props.blockProps.editControl}
                id={props.blockProps.id}
                type={entity.getType()}
            />

        case controls.signature:
            return <SignatureControl
                data={entity.getData()}
                onEdit={props.blockProps.editControl}
                type={entity.getType()}
                id={props.blockProps.id}
            />

        case controls.code:
            return <CodeControl
                data={entity.getData()}
                onEdit={props.blockProps.editControl}
                type={entity.getType()}
                id={props.blockProps.id}
            />

        case controls.event:
            return <EventControl
                data={entity.getData()}
                onEdit={props.blockProps.editControl}
                type={entity.getType()}
                id={props.blockProps.id}
            />

        default:
            return <React.Fragment />
    }
}

const MentionComponent = props => {
    return (
        <span data-offset-key={props.offsetKey} style={{background: 'rgba(0, 0, 0, 0.14)'}}>
            {props.children}
        </span>
    )
}

const LinkComponent = (props) => {
    const {url} = props.contentState.getEntity(props.entityKey).getData()

    return (
        <a href={url} style={{color: '#2996da'}}>
            {props.children}
        </a>
    )
}

const controls = {
    event: 'emailevent',
    button: 'emailbtn',
    question: 'emailquestion',
    signature: 'emailsign',
    gdpr: 'emailreg',
    publictable: 'emailtable',
    publicform: 'emailform',
    code: 'emailcode',
    bold: 'bold',
    underline: 'underline',
    italic: 'italic',
	ul: 'unordered-list-item',
	ol: 'ordered-list-item',
	font: 'font',
	link: 'link',
	color: 'color'
}

const fontSizeStyle = {
	'fontsize-10': {
		fontSize: '10px',
	},
	'fontsize-12': {
		fontSize: '12px',
	},
	'fontsize-14': {
		fontSize: '14px',
	},
	'fontsize-16': {
		fontSize: '16px',
	},
	'fontsize-18': {
		fontSize: '18px',
	},
	'fontsize-20': {
		fontSize: '20px',
	},
	'fontsize-22': {
		fontSize: '22px',
	},
	'fontsize-24': {
		fontSize: '24px',
	},
	'fontsize-26': {
		fontSize: '26px',
	},
	'fontsize-28': {
		fontSize: '28px',
	},
	'fontsize-30': {
		fontSize: '30px',
	}
}

class EmailEditor extends React.Component {
    constructor(props) {
        super(props)

        const substitutions = this.props.subs
        this.substitutionsSorted = [...substitutions].sort((a, b) => b.name.length - a.name.length)

		this.mentionPlugin = createMentionPlugin({
            mentionPrefix: '@',
            entityMutability: 'IMMUTABLE',
			popperOptions: {
				strategy: 'fixed',
				placement: 'bottom-start',
				modifiers: [
					{
						name: 'offset',
						enabled: true,
						options: {
							offset: [0, 5],
						}
					},
					{
						name: 'flip',
						enabled: false
					},
					{
						name: 'styles',
						enabled: true,
						phase: 'write',
						fn: ({state}) => {
							state.styles.popper.maxHeight = '190px'
							state.styles.popper.overflowY = 'auto'
							state.styles.popper.borderRadius = '4px'
							state.styles.popper.boxShadow = '0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)'
						}
					}
				]
			}
        })

        this.decorators = [
            {
                strategy: this.handleStrategy,
                component: MentionComponent,
            },
            {
                strategy: this.handleLink,
                component: LinkComponent,
            }
        ]

        let editorState = EditorState.createEmpty()

        if (props.value) {
            const blocksFromHtml = htmlToDraft(props.value, this.htmlToDraft)
            const { contentBlocks, entityMap } = blocksFromHtml
            const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap)
            editorState = EditorState.createWithContent(this.getMentions(contentState))
        }

        this.data = null

        this.state = {
            editorState,
            suggestions: substitutions,
            substitutions,
            open: false,
            elem: null,
            url: '',
			suggestionsOpen: false,
			target: null
        }
    }

    getMentions = state => {
        let contentState = state

        contentState.getBlockMap().forEach(block => {
            if (block) {
                const text = block.getText()
                this.substitutionsSorted.forEach((sub) => {
                    const mention = '@' + sub.name
                    const entries = [...text.matchAll(new RegExp(mention, 'g'))]
                    entries.forEach(entry => {
                        const anchor = entry.index
                        const focus = anchor + mention.length

                        const blockKey = block.getKey()

                        const selection = new SelectionState({
                            anchorKey: blockKey,
                            anchorOffset: anchor,
                            focusKey: blockKey,
                            focusOffset: focus
                        })

                        contentState.createEntity("mention", "IMMUTABLE", {
                            mention: {
                                name: mention
                            }
                        })

                        const entityKey = contentState.getLastCreatedEntityKey()

                        contentState = Modifier.applyEntity(contentState, selection, entityKey)
                    })
                })
            }
        })

        return contentState
    }

    handleStrategy = (contentBlock, callback, contentState) => {
        const text = contentBlock.getText()
        this.substitutionsSorted.forEach((sub) => {
            const mention = '@' + sub.name
            if (text.includes(mention)) {
                const start = text.indexOf(mention)
                callback(start, start + mention.length)
            }
        })
    }

    handleLink = (contentBlock, callback, contentState) => {
        contentBlock.findEntityRanges(
            (character) => {
                const entityKey = character.getEntity()
                return (
                    entityKey !== null &&
                    contentState.getEntity(entityKey).getType() === 'LINK'
                )
            }, callback
        )
    }

    onChange = (editorState) => {
        this.setState({
            editorState
        }, () => this.handleChange())
    }

    handleChange = () => {
        if (typeof this.props.onChange === 'function') {
            const rawContentState = convertToRaw(this.state.editorState.getCurrentContent())
            const markup = draftToHtml(rawContentState, {}, false, this.draftToHtml)

            if (markup !== this.props.value) {
                this.props.onChange(markup)
            }
        }
    }

    toggleStyle = (e) => {
        const style = e.currentTarget.getAttribute('data-type').toUpperCase()
        this.setState({
            editorState: RichUtils.toggleInlineStyle(this.state.editorState, style)
        }, () => this.editor.focus())
    }

	toggleType = (e) => {
		const type = e.currentTarget.getAttribute('data-type')

		this.setState({
            editorState: RichUtils.toggleBlockType(this.state.editorState, type)
        }, () => this.editor.focus())
	}

    onControlClick = (e) => {
        const type = e.currentTarget.getAttribute('data-type')
        this.setState({
			open: type,
			target: e.currentTarget
		})
    }

    onLinkClick = (e) => {
        e.preventDefault()

        const {editorState} = this.state
        const selection = editorState.getSelection()
        if (!selection.isCollapsed()) {
            const contentState = editorState.getCurrentContent()
            const startKey = editorState.getSelection().getStartKey()
            const startOffset = editorState.getSelection().getStartOffset()
            const blockWithLinkAtBeginning = contentState.getBlockForKey(startKey)
            const linkKey = blockWithLinkAtBeginning.getEntityAt(startOffset)

            let url = ''
            if (linkKey) {
                const linkInstance = contentState.getEntity(linkKey)
                if (linkInstance.getType() === 'mention') {
                    return
                } else {
                    url = linkInstance.getData().url ? linkInstance.getData().url : ''
                }
            }

            this.setState({
                open: controls.link,
                url
            })
        }
    }

    confirmLink = (data) => {
        const {editorState} = this.state

        if (data) {
            const contentState = editorState.getCurrentContent()
            const contentStateWithEntity = contentState.createEntity('LINK', 'MUTABLE', data)
            const entityKey = contentStateWithEntity.getLastCreatedEntityKey()
            const newEditorState = EditorState.set(editorState, { currentContent: contentStateWithEntity })

            this.setState({
                editorState: RichUtils.toggleLink(newEditorState, newEditorState.getSelection(), entityKey),
                open: false,
                url: ''
            }, () => this.editor.focus())
        } else {
            this.setState({
                editorState: RichUtils.toggleLink(editorState, editorState.getSelection(), null),
                open: false,
                url: ''
            }, () => this.editor.focus())
        }
    }

    handleClose = () => {
        this.setState({
            open: false,
            url: ''
        })
    }

    insertBlock = (blockName, blockData) => {
        const editorState = this.state.editorState
        const contentState = editorState.getCurrentContent()
        const contentStateWithEntity = contentState.createEntity(blockName, 'IMMUTABLE', blockData)
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey()
        const newEditorState = EditorState.set(editorState, {
            currentContent: contentStateWithEntity
        })

        this.onChange(AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, ' '))
    }

    blockRendererFn = (block) => {
        if (block.getType() === 'atomic') {
            return {
                component: Media,
                editable: false,
                props: {
                    handleEditorUpdate: this.handleEditorUpdate,
                    editControl: this.editControl,
                    id: block.getKey()
                }
            }
        }

        return null
    }

    editControl = (type, id, data) => {
        this.data = data
        this.setState({open: type, elem: id})
    }

    closeEditControl = () => {
        this.data = null
        this.setState({open: false, elem: null, target: null})
    }

    handleEditorUpdate = (key, data) => {
        let editorState
        const contentState = this.state.editorState.getCurrentContent()
        const contentBlock = contentState.getBlockForKey(key)

        if (data) {
            const entity = contentBlock.getEntityAt(0)
            const state = contentState.mergeEntityData(entity, {...data})
            editorState = EditorState.push(this.state.editorState, state)
        } else {
            const targetRange = new SelectionState({
                anchorKey: key,
                anchorOffset: 0,
                focusKey: key,
                focusOffset: contentBlock.getLength()
            })

            const state = Modifier.removeRange(contentState, targetRange, 'backward')
            const resetBlock = Modifier.setBlockType(state, state.getSelectionAfter(), 'unstyled')
            editorState = EditorState.push(this.state.editorState, resetBlock, 'remove-range')
        }


        this.onChange(EditorState.forceSelection(editorState, editorState.getSelection()))
    }

    onSearchChange = ({ value }) => {
        const val = value.toLowerCase()
        const suggestions = this.state.substitutions.filter(sub => sub.name.toLowerCase().includes(val))
        this.setState({ suggestions })
    }

    handleKeyCommand = (command) => {
        const newState = RichUtils.handleKeyCommand(this.state.editorState, command)

        if (newState) {
            this.onChange(newState)
            return 'handled'
        }

        return 'not-handled'
    }

    htmlToDraft = (nodeName, node) => {
        if (nodeName === controls.button) {
            return {
                type: controls.button,
                mutability: 'IMMUTABLE',
                data: {
                    name: decodeURI(node.attributes.text.value),
                    background: decodeURI(node.attributes.background.value),
                    url: decodeURI(node.attributes.url.value),
                    color: decodeURI(node.attributes.color.value)
                }
            }
        }

        if (nodeName === controls.publicform) {
            return {
                type: controls.publicform,
                mutability: 'IMMUTABLE',
                data: {
                    value: node.attributes.value.value,
                    name: decodeURI(node.attributes.name.value),
                    background: decodeURI(node.attributes.background.value),
                    color: decodeURI(node.attributes.color.value)
                }
            }
        }

        if (nodeName === controls.publictable) {
            return {
                type: controls.publictable,
                mutability: 'IMMUTABLE',
                data: {
                    value: node.attributes.value.value,
                    name: decodeURI(node.attributes.name.value),
                    background: decodeURI(node.attributes.background.value),
                    color: decodeURI(node.attributes.color.value)
                }
            }
        }

        if (nodeName === controls.gdpr) {
            return {
                type: controls.gdpr,
                mutability: 'IMMUTABLE',
                data: {
                    positive: decodeURI(node.attributes.positive.value),
                    negative: decodeURI(node.attributes.negative.value)
                }
            }
        }

        if (nodeName === controls.question) {
            return {
                type: controls.question,
                mutability: 'IMMUTABLE',
                data: {
                    positive: decodeURI(node.attributes.positive.value),
                    negative: decodeURI(node.attributes.negative.value),
                    field: node.attributes.field.value
                }
            }
        }

        if (nodeName === controls.signature) {
            return {
                type: controls.signature,
                mutability: 'IMMUTABLE',
                data: {
                    value: node.attributes.value.value
                }
            }
        }

        if (nodeName === controls.code) {
            return {
                type: controls.code,
                mutability: 'IMMUTABLE',
                data: {
                    value: node.innerHTML
                }
            }
        }

        if (nodeName === controls.event) {
            return {
                type: controls.event,
                mutability: 'IMMUTABLE',
                data: {
                    title: decodeURI(node.attributes.title.value),
                    from: node.attributes.from.value,
                    to: node.attributes.to.value,
                    duration: node.attributes.duration.value,
                    description: decodeURI(node.attributes.description.value)
                }
            }
        }
    }

    draftToHtml = (entity) => {
        if (entity.type === controls.button) {
            const {name, background, url, color} = entity.data
            return `<emailbtn text="${encodeURI(name)}" url="${encodeURI(url)}" background="${encodeURI(background)}" color="${encodeURI(color)}"></emailbtn>`
        }

        if (entity.type === controls.publicform) {
            const {value, name, background, color} = entity.data
            return `<emailform value="${value}" name="${encodeURI(name)}" background="${encodeURI(background)}" color="${encodeURI(color)}"></emailform>`
        }

        if (entity.type === controls.publictable) {
            const {value, name, background, color} = entity.data
            return `<emailtable value="${value}" name="${encodeURI(name)}" background="${encodeURI(background)}" color="${encodeURI(color)}"></emailtable>`
        }

        if (entity.type === controls.gdpr) {
            const {positive, negative} = entity.data
            return `<emailreg positive="${encodeURI(positive)}" negative="${encodeURI(negative)}"></emailreg>`
        }

        if (entity.type === controls.question) {
            const {positive, negative, field} = entity.data
            return `<emailquestion positive="${encodeURI(positive)}" negative="${encodeURI(negative)}" field="${field}"></emailquestion>`
        }

        if (entity.type === controls.signature) {
            const {value} = entity.data
            return `<emailsign value="${value}"></emailsign>`
        }

        if (entity.type === controls.code) {
            const {value} = entity.data
            return `<emailcode>${value}</emailcode>`
        }

        if (entity.type === controls.event) {
            const {title, from, to, duration, description} = entity.data
            return `<emailevent title="${encodeURI(title)}" from="${from}" to="${to}" duration="${duration}" description="${encodeURI(description)}"></emailevent>`
        }
    }

    handlePastedText = (text, html, editorState) => {
        const state = handleDraftEditorPastedText(html, editorState)

        if (state) {
            this.onChange(state)

            return 'handled'
        }

        return 'not-handled'
    }

	onOpenChange = (open) => {
		if (open) {
			const el = document.getElementById('editor')
        	el.style.overflow = 'hidden'
		} else {
			const el = document.getElementById('editor')
        	el.style.overflow = ''
		}

		this.setState({suggestionsOpen: open})
	}

	setFont = (size) => {
		this.closeEditControl()
		this.onToggle(size, fontSizeStyle)
	}

	setColor = (hex) => {
		const style = Object.keys(colorStyle).find(item => colorStyle[item].color === hex)
		this.closeEditControl()
		this.onToggle(style, colorStyle)
	}

	onToggle = (style, styleMap) => {
		const {editorState} = this.state
		const selection = editorState.getSelection()

		const nextContentState = Object.keys(styleMap).reduce((contentState, item) => {
			return Modifier.removeInlineStyle(contentState, selection, item)
		}, editorState.getCurrentContent())

		let nextEditorState = EditorState.push(
			editorState,
			nextContentState,
			'change-inline-style'
		)

		const currentStyle = editorState.getCurrentInlineStyle()

		// Unset style override for current color.
		// if (selection.isCollapsed()) {
		// 	nextEditorState = currentStyle.reduce((state, item) => {
		// 		return RichUtils.toggleInlineStyle(state, item)
		// 	}, nextEditorState)
		// }

		if (!currentStyle.has(style)) {
			nextEditorState = RichUtils.toggleInlineStyle(
				nextEditorState,
				style
			)
		}

		this.setState({
			editorState: nextEditorState,
		}, () => this.editor.focus())
	}

    render() {
        const {intl} = this.props
        const {MentionSuggestions} = this.mentionPlugin
        const plugins = [this.mentionPlugin]
		const currentInlineStyle = this.state.editorState.getCurrentInlineStyle()

        return (
            <React.Fragment>
                <Panel>
                    <Action onClick={this.toggleStyle} data-type={controls.bold}>
                        <FormatBold style={{ fontSize: 20 }} />
                    </Action>
                    <Action onClick={this.toggleStyle} data-type={controls.underline}>
                        <FormatUnderlined style={{ fontSize: 20 }} />
                    </Action>
                    <Action onClick={this.toggleStyle} data-type={controls.italic}>
                        <FormatItalic style={{ fontSize: 20 }} />
                    </Action>
					<Action onClick={this.onControlClick} data-type={controls.font}>
                        <TextFormat style={{ fontSize: 20 }} />
                    </Action>
					<Action onClick={this.onControlClick} data-type={controls.color}>
                        <Palette style={{ fontSize: 20 }} />
                    </Action>
					<Action onClick={this.toggleType} data-type={controls.ul}>
                        <FormatListBulleted style={{ fontSize: 20 }} />
                    </Action>
					<Action onClick={this.toggleType} data-type={controls.ol}>
                        <FormatListNumbered style={{ fontSize: 20 }} />
                    </Action>
                    <Tooltip
                        title={intl.formatMessage({id: "editor.code"})}
                        placement="top"
                    >
                        <Action onClick={this.onControlClick} data-type={controls.code}>
                            <Code style={{ fontSize: 20 }} />
                        </Action>
                    </Tooltip>
                    <Tooltip
                        title={intl.formatMessage({id: "editor.link"})}
                        placement="top"
                    >
                        <Action onClick={this.onLinkClick}>
                            <Link style={{ fontSize: 20 }} />
                        </Action>
                    </Tooltip>
                    {!!this.props.dates.length &&
                        <Tooltip
                            title={intl.formatMessage({id: "editor.event"})}
                            placement="top"
                        >
                            <Action onClick={this.onControlClick} data-type={controls.event} style={{ marginLeft: 'auto' }}>
                                <Event />
                            </Action>
                        </Tooltip>
                    }
                    <Tooltip
                        title={intl.formatMessage({id: "editor.button"})}
                        placement="top"
                    >
                        <Action onClick={this.onControlClick} data-type={controls.button}>
                            <Crop169 />
                        </Action>
                    </Tooltip>
                    <Tooltip
                        title={intl.formatMessage({id: "editor.gdpr"})}
                        placement="top"
                    >
                        <Action onClick={this.onControlClick} data-type={controls.gdpr}>
                            <Security />
                        </Action>
                    </Tooltip>
                    {!!this.props.fields.length &&
                        <Tooltip
                            title={intl.formatMessage({id: "editor.question"})}
                            placement="top"
                        >
                            <Action onClick={this.onControlClick}  data-type={controls.question}>
                                <HelpOutline />
                            </Action>
                        </Tooltip>
                    }
                    {!!this.props.forms.length &&
                        <Tooltip
                            title={intl.formatMessage({id: "editor.public_form"})}
                            placement="top"
                        >
                            <Action onClick={this.onControlClick} data-type={controls.publicform}>
                                <Reorder />
                            </Action>
                        </Tooltip>
                    }
                    {!!this.props.tables.length &&
                        <Tooltip
                            title={intl.formatMessage({id: "editor.public_table"})}
                            placement="top"
                        >
                            <Action onClick={this.onControlClick} data-type={controls.publictable}>
                                <People />
                            </Action>
                        </Tooltip>
                    }
                    <Tooltip
                        title={intl.formatMessage({id: "editor.sign"})}
                        placement="top"
                    >
                        <Action onClick={this.onControlClick} data-type={controls.signature}>
                            <Assignment />
                        </Action>
                    </Tooltip>
                </Panel>
                <Container>
                    <Content ref={el => this.container = el} id='editor'>
                        <Editor
                            ref={el => this.editor = el}
                            editorState={this.state.editorState}
                            onChange={this.onChange}
                            blockRendererFn={this.blockRendererFn}
                            plugins={plugins}
                            handleKeyCommand={this.handleKeyCommand}
                            decorators={this.decorators}
                            handlePastedText={this.handlePastedText}
							customStyleMap={{...fontSizeStyle, ...colorStyle}}
							onCopy={onDraftEditorCopy}
        					onCut={onDraftEditorCut}
                            // onBlur={this.handleChange}
                        />
					</Content>
					<MentionSuggestions
						open={this.state.suggestionsOpen}
						onOpenChange={this.onOpenChange}
						onSearchChange={this.onSearchChange}
						suggestions={this.state.suggestions}
						entryComponent={Entry}
					/>
                </Container>
                {this.state.open === controls.link &&
                    <LinkDialog
                        open={true}
                        handleClose={this.handleClose}
                        value={this.state.url}
                        handleConfirm={this.confirmLink}
                    />
                }
                {this.state.open === controls.button &&
                    <ButtonDialog
                        open={true}
                        handleClose={this.closeEditControl}
                        options={this.props.urls}
                        data={this.data}
                        id={this.state.elem}
                        handleEditorUpdate={this.handleEditorUpdate}
                        insertBlock={this.insertBlock}
                        type={this.state.open}
                    />
                }
                {this.state.open === controls.question &&
                    <QuestionDialog
                        open={true}
                        handleClose={this.closeEditControl}
                        options={this.props.fields}
                        data={this.data}
                        id={this.state.elem}
                        handleEditorUpdate={this.handleEditorUpdate}
                        insertBlock={this.insertBlock}
                        type={this.state.open}
                    />
                }
                {this.state.open === controls.signature &&
                    <SignatureDialog
                        open={true}
                        handleClose={this.closeEditControl}
                        data={this.data}
                        id={this.state.elem}
                        handleEditorUpdate={this.handleEditorUpdate}
                        insertBlock={this.insertBlock}
                        type={this.state.open}
                    />
                }
                {(this.state.open === controls.publictable || this.state.open === controls.publicform) &&
                    <PublicEntityDialog
                        open={true}
                        handleClose={this.closeEditControl}
                        data={this.data}
                        id={this.state.elem}
                        handleEditorUpdate={this.handleEditorUpdate}
                        insertBlock={this.insertBlock}
                        type={this.state.open}
                        options={this.state.open === controls.publictable ? this.props.tables : this.props.forms}
                    />
                }
                {this.state.open === controls.gdpr &&
                    <RegulationDialog
                        open={true}
                        handleClose={this.closeEditControl}
                        data={this.data}
                        id={this.state.elem}
                        handleEditorUpdate={this.handleEditorUpdate}
                        insertBlock={this.insertBlock}
                        type={this.state.open}
                    />
                }
                {this.state.open === controls.code &&
                    <CodeDialog
                        open={true}
                        handleClose={this.closeEditControl}
                        data={this.data}
                        id={this.state.elem}
                        handleEditorUpdate={this.handleEditorUpdate}
                        insertBlock={this.insertBlock}
                        type={this.state.open}
                    />
                }
                {this.state.open === controls.event &&
                    <EventDialog
                        open={true}
                        handleClose={this.closeEditControl}
                        data={this.data}
                        id={this.state.elem}
                        handleEditorUpdate={this.handleEditorUpdate}
                        insertBlock={this.insertBlock}
                        type={this.state.open}
                        subs={this.props.subs}
                        numbers={this.props.numbers}
                        dates={this.props.dates}
                    />
                }
				{this.state.open === controls.font &&
                    <FontPopover
						style={currentInlineStyle}
                        handleClose={this.closeEditControl}
                        target={this.state.target}
                        onToggle={this.setFont}
                    />
                }
				{this.state.open === controls.color &&
                    <ColorPopover
						style={colorStyle[Object.keys(colorStyle).find(style => currentInlineStyle.has(style))]}
                        handleClose={this.closeEditControl}
                        target={this.state.target}
                        onToggle={this.setColor}
                    />
                }
            </React.Fragment>
        )
    }
}

export default injectIntl(EmailEditor)
